import React, { useCallback, useState } from 'react'
import { observer } from 'mobx-react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

import { useStores, useRequest } from 'admin/hooks'
import * as srv from 'admin/services'

import CustomizedAutocomplete from 'admin/components/autocomplete'
import CustomizedDialogTitle from 'admin/components/dialog-title'
import TextField from 'admin/components/textfield'
import styles from './styles'

const useStyles = makeStyles(styles)

const Filter = () => {
  const classes = useStyles()
  const {
    invoicePaymentStore,
  } = useStores()

  const [status, setStatus] = useState('')
  const [paymentRef, setPaymentRef] = useState('')
  const [driverOptions, setDriverOptions] = useState([])
  const [drivers, setDrivers] = useState(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')

  const {
    request: fetchDriver,
    loading: driverLoading,
  } = useRequest(srv.fetchDriver, {
    concurrent: true,
    initialData: { list: [], total: 0 },
    transformData: (data) => {
      if (data && data.list.length > 0) {
        setDriverOptions(data.list.map(item => ({
          key: item.id,
          label: item.code,
        })))
      }
    },
  })

  const handleOpenSearchDriver = useCallback(() => {
    fetchDriver({ excludeStatus: 'achieved' })
  }, [])

  const handleSearchDriverInputChange = useCallback((event, newValue) => {
    if (!newValue) {
      setDriverOptions([])
      return
    }
    fetchDriver({ code: newValue, excludeStatus: 'achieved' })
  }, [])

  const handleChangeSearchDriver = useCallback((_, newValue) => {
    setDrivers(newValue)
  }, [])

  const handleClose = useCallback(() => {
    invoicePaymentStore.setCloseDialog('filter')
  }, [])

  const handleSubmit = useCallback(() => {
    invoicePaymentStore.updateQuery('paymentRef', paymentRef.trim(), true)
    invoicePaymentStore.updateQuery('paymentStatus', status, true)
    invoicePaymentStore.updateQuery('driverId', drivers?.key, true)
    invoicePaymentStore.updateQuery('invoiceNumber', invoiceNumber.trim(), true)

    invoicePaymentStore.setCloseDialog('filter')
  }, [status, drivers, paymentRef, invoiceNumber])

  const handleReset = useCallback(() => {
    setPaymentRef('')
    setDrivers(null)
    setStatus('')
    setInvoiceNumber('')
  }, [])

  if (!invoicePaymentStore.dialog.filter) return null

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={invoicePaymentStore.dialog.filter}
      onClose={handleClose}
      aria-labelledby="Filter"
    >
      <CustomizedDialogTitle
        onReset={handleReset}
        resetDisabled={!invoicePaymentStore.filterLength}
      >
        Payment Filter
      </CustomizedDialogTitle>
      <DialogContent>
        <TextField
          name="paymentRef"
          type="text"
          variant="outlined"
          size="small"
          fullWidth
          label="Payment Ref"
          value={paymentRef}
          onChange={(e) => {
            setPaymentRef(e.target.value)
          }}
          className={classes.textfield}
        />
        <CustomizedAutocomplete
          label="Driver"
          size="small"
          value={drivers}
          handleOpen={handleOpenSearchDriver}
          handleInputChange={handleSearchDriverInputChange}
          handleChange={handleChangeSearchDriver}
          options={driverOptions}
          loading={driverLoading}
          className={classes.autocomplete}
        />
        <TextField
          name="status"
          type="text"
          variant="outlined"
          size="small"
          fullWidth
          select
          label="Payment Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
          }}
          className={classes.textfield}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="unpaid">Unpaid</MenuItem>
          <MenuItem value="received">Received</MenuItem>
          <MenuItem value="partial">Partial</MenuItem>
          <MenuItem value="excess">Excess</MenuItem>
        </TextField>
        <TextField
          name="invoiceNumber"
          type="text"
          variant="outlined"
          size="small"
          fullWidth
          label="Invoice Number"
          value={invoiceNumber}
          onChange={(e) => {
            setInvoiceNumber(e.target.value)
          }}
          className={classes.textfield}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          color="secondary"
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default observer(Filter)
